import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useParams } from 'react-router';
import { api } from '../api';
import { getAccessToken } from '../auth';
import { getConfig } from '../config';
import { formatElapsed } from '../format';
import { type Outfit, describeOutfit } from '@splitline/core';
import type { Runner, TimingPoint } from '../types';
import { liveKey, useLive } from './queries';

interface CrossingView {
  id: string;
  timeMs: number;
  timeSource: string;
  status: string;
  bibReads: { number: string; confidence: number }[];
  outfit: Record<string, unknown> | null;
  cropUrls: string[];
  identityOnly: boolean;
  score?: number;
  pace?: number | null;
  outfitMatch?: number | null;
  modelReason?: string | null;
  modelConfidence?: number | null;
}

interface RunnerRef {
  id: string;
  bib: string | null;
  name: string;
  division: string | null;
  description: string | null;
}

/** What a runner looked like where cameras identified them before. */
interface Look {
  photos: string[];
  seenAs: string | null;
  seenAt: string | null;
}

interface ReviewItem {
  id: string;
  kind: 'missing' | 'unknown' | 'conflict' | 'low_conf';
  status: string;
  timingPoint: TimingPoint | null;
  runner: RunnerRef | null;
  runnerLook: Look | null;
  crossing: CrossingView | null;
  candidates: { runner: RunnerRef | null; score: number | null; reason: string | null; rank: number; fromModel: boolean; look: Look }[];
  nominations: CrossingView[];
}

interface ResultsData {
  points: { id: string; name: string; km: number; kind: string }[];
  rows: {
    runnerId: string;
    bib: string | null;
    name: string;
    division: string | null;
    splits: Record<string, number | null>;
    gunMs: number | null;
    netMs: number | null;
    place: number | null;
    divisionPlace: number | null;
    unconfirmed: boolean;
  }[];
}

const kindLabel: Record<ReviewItem['kind'], [string, string]> = {
  missing: ['Missing crossing', 'bad'],
  unknown: ['Unknown runner', 'warn'],
  conflict: ['Conflict', 'warn'],
  low_conf: ['Low confidence', 'warn'],
};

/** A runner missing only at the start just loses their net time: say so, and keep it out of the way. */
const isStartOnly = (i: ReviewItem) => i.kind === 'missing' && i.timingPoint?.kind === 'start';
const labelOf = (i: ReviewItem): [string, string] => (isStartOnly(i) ? ['Missing start · net time only', 'neutral'] : kindLabel[i.kind]);

/** "49:52.3", "1:02:03.45" or "2992.3" (seconds) → ms. */
function parseTime(s: string): number | null {
  const parts = s.trim().split(':').map(Number);
  if (!parts.length || parts.some((p) => !Number.isFinite(p))) return null;
  return Math.round(parts.reduce((acc, p) => acc * 60 + p, 0) * 1000);
}

function Crops({ urls, height = 180 }: { urls: string[]; height?: number }) {
  if (!urls.length) return <div className="small muted">No crops.</div>;
  return (
    <div className="inline" style={{ alignItems: 'flex-start' }}>
      {urls.map((u, i) => (
        <a key={u} href={u} target="_blank" rel="noreferrer">
          <img src={u} alt={`Crop ${i + 1} of the runner`} style={{ height, borderRadius: 8, background: 'var(--line-soft)' }} />
        </a>
      ))}
    </div>
  );
}

function RunnerSearch({ raceId, onPick }: { raceId: string; onPick: (r: Runner) => void }) {
  const [q, setQ] = useState('');
  const found = useQuery({
    queryKey: ['runner-search', raceId, q],
    queryFn: () => api<{ runners: Runner[] }>(`/races/${raceId}/runners?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 1,
  });
  return (
    <div className="stack" style={{ gap: 6 }}>
      <input className="input" placeholder="Find a runner by bib, name or outfit" aria-label="Find a runner" value={q} onChange={(e) => setQ(e.target.value)} />
      {(found.data?.runners ?? []).slice(0, 6).map((r) => (
        <button key={r.id} type="button" className="btn small" style={{ justifyContent: 'flex-start' }} onClick={() => onPick(r)}>
          <span className="bib">{r.bib ?? '—'}</span> {r.name} <span className="muted small">{r.descriptionText ?? ''}</span>
        </button>
      ))}
    </div>
  );
}

function ItemDetail({ item, raceId, gun }: { item: ReviewItem; raceId: string; gun: number | null }) {
  const qc = useQueryClient();
  const [manual, setManual] = useState('');
  const [manualRunner, setManualRunner] = useState<RunnerRef | null>(item.runner);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['review', raceId] });
    qc.invalidateQueries({ queryKey: liveKey(raceId) });
    qc.invalidateQueries({ queryKey: ['results', raceId] });
  };
  const assign = useMutation({ mutationFn: (b: { runnerId: string; crossingId?: string }) => api(`/review/${item.id}/assign`, { body: b }), onSuccess: refresh });
  const dismiss = useMutation({ mutationFn: () => api(`/review/${item.id}/dismiss`, { body: {} }), onSuccess: refresh });
  const askModel = useMutation({ mutationFn: () => api(`/review/${item.id}/ask-model`, { body: {} }), onSuccess: refresh });
  const askButton = (label: string) => (
    <div className="inline">
      <button type="button" className="btn" onClick={() => askModel.mutate()} disabled={askModel.isPending}>
        {askModel.isPending ? 'Looking…' : label}
      </button>
      <span className="small muted">Claude compares the photos with what’s on file. You still decide.</span>
    </div>
  );
  const nudge = useMutation({ mutationFn: (deltaMs: number) => api(`/crossings/${item.crossing!.id}/nudge`, { body: { deltaMs } }), onSuccess: refresh });
  const saveManual = useMutation({
    mutationFn: () => {
      const rel = parseTime(manual);
      if (rel === null || !manualRunner) throw new Error('Enter a time like 49:52.30 and choose a runner');
      return api(`/review/${item.id}/manual`, { body: { runnerId: manualRunner.id, timeMs: (gun ?? 0) + rel } });
    },
    onSuccess: refresh,
  });
  const t = (ms: number) => (gun ? formatElapsed(ms - gun, 2) : new Date(ms).toLocaleTimeString());
  const [label, tone] = labelOf(item);
  const err = (assign.error ?? dismiss.error ?? nudge.error ?? saveManual.error ?? askModel.error) as Error | null;

  return (
    <div className="stack" style={{ gap: 16 }}>
      <section className="card stack">
        <div className="inline" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <span className={`chip ${tone}`}>{label}</span>
            <h2 className="display" style={{ fontSize: 30, marginTop: 6 }}>
              {item.runner ? `${item.runner.bib ? `#${item.runner.bib} ` : ''}${item.runner.name}` : 'Unknown runner'}
            </h2>
            <div className="muted">
              {item.timingPoint?.name}
              {item.crossing ? ` · crossed at ${t(item.crossing.timeMs)}` : ''}
              {item.crossing?.identityOnly ? ' · seen by the bib camera only (time approximate)' : ''}
            </div>
          </div>
          {item.runner?.description && (
            <div className="card" style={{ background: 'var(--ground)', padding: '10px 14px', maxWidth: 320 }}>
              <div className="small muted">Description on file</div>
              <div>{item.runner.description}</div>
            </div>
          )}
        </div>
        {item.runnerLook && item.runnerLook.photos.length > 0 && (
          <div className="stack" style={{ gap: 6 }}>
            <div className="small muted">
              How they looked at {item.runnerLook.seenAt}
              {item.runnerLook.seenAs ? `: ${item.runnerLook.seenAs}` : ''}
            </div>
            <Crops urls={item.runnerLook.photos} height={140} />
          </div>
        )}
        {item.crossing && (
          <>
            <Crops urls={item.crossing.cropUrls} />
            <div className="small">
              Bib read: {item.crossing.bibReads.length ? item.crossing.bibReads.map((b) => `${b.number} (${Math.round(b.confidence * 100)}%)`).join(', ') : 'none'}
              {item.crossing.outfit ? ` · Seen wearing: ${describeOutfit(item.crossing.outfit as Outfit)}` : ''}
            </div>
            <div className="inline">
              <span className="small muted">Adjust time:</span>
              {[-100, -33, 33, 100].map((d) => (
                <button key={d} type="button" className="btn small" onClick={() => nudge.mutate(d)} disabled={nudge.isPending}>
                  {d > 0 ? '+' : '−'}
                  {Math.abs(d) === 33 ? '1 frame' : '0.1 s'}
                </button>
              ))}
            </div>
          </>
        )}
      </section>

      {item.kind === 'missing' && (
        <section className="card stack">
          <h3 style={{ margin: 0 }}>Possible crossings for this runner</h3>
          <div className="small muted">Unclaimed crossings at {item.timingPoint?.name} in the window their pace predicts, best match first.</div>
          {item.nominations.length === 0 && <div className="muted">Nothing unclaimed in that window. Enter a time by hand below, or dismiss if they didn’t pass this point.</div>}
          {item.nominations.length > 0 && askButton('Ask the model to look')}
          <div className="grid-cards">
            {item.nominations.map((n) => (
              <div key={n.id} className="card stack" style={{ gap: 8, border: n.modelReason ? '2px solid var(--accent)' : undefined }}>
                {n.modelReason && (
                  <div className="small">
                    <span className="chip info">Model’s pick {n.modelConfidence != null ? `· ${Math.round(n.modelConfidence * 100)}%` : ''}</span> {n.modelReason}
                  </div>
                )}
                <Crops urls={n.cropUrls.slice(0, 2)} />
                <div className="mono">{t(n.timeMs)}</div>
                <div className="small muted">
                  {n.bibReads.map((b) => b.number).join(', ') || 'no bib read'} · pace fit {n.pace != null ? Math.round(n.pace * 100) : '—'}% · outfit {n.outfitMatch != null ? Math.round(n.outfitMatch * 100) : '—'}%
                </div>
                <button type="button" className="btn primary small" onClick={() => assign.mutate({ runnerId: item.runner!.id, crossingId: n.id })}>
                  Use this crossing
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {item.kind !== 'missing' && (
        <section className="card stack">
          <h3 style={{ margin: 0 }}>Who is it?</h3>
          {item.crossing && item.crossing.cropUrls.length > 0 && askButton('Ask the model who this is')}
          {item.candidates.map((c) =>
            c.runner ? (
              <div key={`${c.runner.id}-${c.rank}`} className="inline" style={{ justifyContent: 'space-between', borderTop: '1px solid var(--line-soft)', paddingTop: 10, alignItems: 'flex-start' }}>
                <div className="inline" style={{ alignItems: 'flex-start', gap: 12, flexWrap: 'nowrap' }}>
                  {c.look.photos.length > 0 && <Crops urls={c.look.photos} height={110} />}
                  <div>
                    {c.fromModel && <span className="chip info">Model’s pick</span>} <span className="bib">{c.runner.bib ?? '—'}</span> <b>{c.runner.name}</b>
                    {c.look.seenAs ? (
                      <div className="small">
                        At {c.look.seenAt}: {c.look.seenAs}
                      </div>
                    ) : (
                      c.runner.description && <div className="small muted">On file: {c.runner.description}</div>
                    )}
                    <div className="small muted">
                      {c.reason} · {c.fromModel ? 'confidence' : 'score'} {c.score?.toFixed(2)}
                    </div>
                  </div>
                </div>
                <button type="button" className="btn primary small" onClick={() => assign.mutate({ runnerId: c.runner!.id })} disabled={assign.isPending}>
                  Assign
                </button>
              </div>
            ) : null,
          )}
          <RunnerSearch raceId={raceId} onPick={(r) => assign.mutate({ runnerId: r.id })} />
        </section>
      )}

      <section className="card stack">
        <h3 style={{ margin: 0 }}>Enter a time by hand</h3>
        <div className="form-grid">
          <label className="field">
            Gun time
            <input className="input mono" placeholder="49:52.30" value={manual} onChange={(e) => setManual(e.target.value)} />
          </label>
          <div className="field">
            Runner
            {manualRunner ? (
              <div className="inline">
                <span className="bib">{manualRunner.bib ?? '—'}</span> {manualRunner.name}
                <button type="button" className="btn link" onClick={() => setManualRunner(null)}>
                  change
                </button>
              </div>
            ) : (
              <RunnerSearch raceId={raceId} onPick={(r) => setManualRunner({ id: r.id, bib: r.bib, name: r.name, division: r.division, description: r.descriptionText })} />
            )}
          </div>
          <button type="button" className="btn" onClick={() => saveManual.mutate()} disabled={saveManual.isPending}>
            Save manual time
          </button>
        </div>
      </section>

      {err && <div className="notice bad">{err.message}</div>}
      <div>
        <button type="button" className="btn" onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>
          {item.kind === 'missing' ? 'Didn’t pass this point' : 'Not a runner — ignore'}
        </button>
      </div>
    </div>
  );
}

function PublicResultsPanel({ raceId }: { raceId: string }) {
  const qc = useQueryClient();
  const race = useQuery({ queryKey: ['race', raceId], queryFn: () => api<{ race: { settings: { publicResults?: boolean } } }>(`/races/${raceId}`) });
  const on = race.data?.race.settings.publicResults ?? false;
  const url = `${location.origin}/results/${raceId}`;
  const [qr, setQr] = useState<string | null>(null);
  useEffect(() => {
    if (on) QRCode.toDataURL(url, { margin: 1, width: 200 }).then(setQr, () => setQr(null));
  }, [on, url]);
  const toggle = useMutation({
    mutationFn: (v: boolean) => api(`/races/${raceId}`, { method: 'PATCH', body: { settings: { publicResults: v } } }),
    // Stay "pending" until the refetch lands, so the box doesn't flick back.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['race', raceId] }),
  });
  return (
    <section className="card stack">
      <label className="check" style={{ alignItems: 'center' }}>
        <input type="checkbox" checked={toggle.isPending ? !!toggle.variables : on} onChange={(e) => toggle.mutate(e.target.checked)} disabled={toggle.isPending || !race.data} />
        <span>
          <b>Public live results</b> — anyone with the link sees places and times (names, bibs, divisions). Marked provisional until you close the race.
        </span>
      </label>
      {on && (
        <div className="inline" style={{ gap: 16 }}>
          {qr && <img src={qr} width={120} height={120} alt="QR code for the public results page" style={{ background: '#fff', borderRadius: 8 }} />}
          <div className="stack" style={{ gap: 6 }}>
            <a href={url} target="_blank" rel="noreferrer" className="mono small">
              {url}
            </a>
            <button type="button" className="btn small" onClick={() => navigator.clipboard?.writeText(url)}>
              Copy link
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ManualTime({ raceId, runnerId, points, gun, onDone }: { raceId: string; runnerId: string; points: ResultsData['points']; gun: number | null; onDone: () => void }) {
  const finish = points.find((p) => p.kind === 'finish') ?? points.at(-1);
  const [pointId, setPointId] = useState(finish?.id ?? '');
  const [text, setText] = useState('');
  const save = useMutation({
    mutationFn: () => {
      const rel = parseTime(text);
      if (rel === null || !gun) throw new Error(gun ? 'Enter a time like 52:07.4' : 'Fire the gun first: manual times are gun times');
      return api(`/races/${raceId}/manual-time`, { body: { runnerId, timingPointId: pointId, timeMs: gun + rel } });
    },
    onSuccess: onDone,
  });
  return (
    <div className="inline" style={{ gap: 6 }}>
      <select className="input" style={{ width: 'auto', minHeight: 36 }} value={pointId} onChange={(e) => setPointId(e.target.value)} aria-label="Timing point">
        {points.filter((p) => p.kind !== 'start').map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <input className="input mono" style={{ width: 120, minHeight: 36 }} placeholder="52:07.4" aria-label="Gun time" value={text} onChange={(e) => setText(e.target.value)} autoFocus />
      <button type="button" className="btn small primary" onClick={() => save.mutate()} disabled={save.isPending}>
        Save
      </button>
      {save.error && <span className="small" style={{ color: 'var(--bad-fg)' }}>{(save.error as Error).message}</span>}
    </div>
  );
}

function ResultsTable({ raceId, gun, onFind }: { raceId: string; gun: number | null; onFind: (itemId: string) => void }) {
  const qc = useQueryClient();
  const res = useQuery({ queryKey: ['results', raceId], queryFn: () => api<ResultsData>(`/races/${raceId}/results`), refetchInterval: 10_000 });
  const [q, setQ] = useState('');
  const [show, setShow] = useState<'all' | 'finished' | 'unfinished'>('all');
  const [editing, setEditing] = useState<string | null>(null);
  const find = useMutation({
    mutationFn: ({ runnerId, timingPointId }: { runnerId: string; timingPointId: string }) => api<{ reviewItemId: string }>(`/races/${raceId}/runners/${runnerId}/find`, { body: { timingPointId } }),
    onSuccess: ({ reviewItemId }) => {
      qc.invalidateQueries({ queryKey: ['review', raceId] });
      onFind(reviewItemId);
    },
  });
  const clear = useMutation({
    mutationFn: ({ runnerId, timingPointId }: { runnerId: string; timingPointId: string }) => api(`/races/${raceId}/runners/${runnerId}/clear`, { body: { timingPointId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['results', raceId] });
      qc.invalidateQueries({ queryKey: ['review', raceId] });
    },
  });
  const download = async () => {
    const { apiUrl, authMode } = getConfig();
    const headers: Record<string, string> = authMode === 'dev' ? { 'x-dev-user': localStorage.getItem('splitline.devUser') ?? 'dev-user' } : { authorization: `Bearer ${await getAccessToken()}` };
    const r = await fetch(`${apiUrl}/api/races/${raceId}/results.csv`, { headers });
    const url = URL.createObjectURL(await r.blob());
    Object.assign(document.createElement('a'), { href: url, download: 'results.csv' }).click();
    URL.revokeObjectURL(url);
  };
  if (!res.data) return <p className="empty">Loading…</p>;
  const pts = res.data.points.filter((p) => p.kind !== 'start');
  const finish = res.data.points.find((p) => p.kind === 'finish');
  const needle = q.trim().toLowerCase();
  const rows = res.data.rows.filter(
    (r) =>
      (show === 'all' || (show === 'finished') === (r.gunMs !== null)) &&
      (!needle || `${r.bib ?? ''} ${r.name} ${r.division ?? ''}`.toLowerCase().includes(needle)),
  );
  const unfinished = res.data.rows.filter((r) => r.gunMs === null).length;
  return (
    <div className="stack" style={{ gap: 16 }}>
      <PublicResultsPanel raceId={raceId} />
      <section className="card flush">
        <div className="card-head" style={{ alignItems: 'center' }}>
          <h2 style={{ marginRight: 'auto' }}>Results</h2>
          <select className="input" style={{ width: 'auto' }} value={show} onChange={(e) => setShow(e.target.value as typeof show)} aria-label="Show">
            <option value="all">Everyone ({res.data.rows.length})</option>
            <option value="finished">Finished ({res.data.rows.length - unfinished})</option>
            <option value="unfinished">No finish time ({unfinished})</option>
          </select>
          <input className="input" type="search" placeholder="Search bib, name, division" aria-label="Search results" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240, maxWidth: '100%' }} />
          <button type="button" className="btn small" onClick={download}>
            Export CSV
          </button>
        </div>
        <div className="table-wrap">
          <table className="data" style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th>Place</th>
                <th>Bib</th>
                <th>Runner</th>
                <th className="num">Div. place</th>
                {pts.map((p) => (
                  <th key={p.id} className="num">
                    {p.name}
                  </th>
                ))}
                <th className="num">Net</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.runnerId}>
                  <td className="mono">{r.place ?? ''}</td>
                  <td>
                    <span className="bib">{r.bib ?? '—'}</span>
                  </td>
                  <td>
                    <b>{r.name}</b> {r.unconfirmed && <span className="chip neutral" title="Matched automatically; not yet checked by a person">auto</span>}
                    <div className="small muted">{r.division}</div>
                  </td>
                  <td className="num mono">{r.divisionPlace ?? ''}</td>
                  {pts.map((p) => (
                    <td key={p.id} className="num mono">
                      {r.splits[p.id] != null ? formatElapsed(r.splits[p.id]!, 2) : '—'}
                    </td>
                  ))}
                  <td className="num mono">{r.netMs !== null ? formatElapsed(r.netMs, 2) : '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {editing === r.runnerId ? (
                      <ManualTime raceId={raceId} runnerId={r.runnerId} points={res.data!.points} gun={gun} onDone={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['results', raceId] }); }} />
                    ) : (
                      <div className="inline" style={{ gap: 6, flexWrap: 'nowrap' }}>
                        <button type="button" className="btn small" onClick={() => setEditing(r.runnerId)}>
                          Enter time
                        </button>
                        {r.gunMs === null && finish && (
                          <button type="button" className="btn small" onClick={() => find.mutate({ runnerId: r.runnerId, timingPointId: finish.id })} disabled={find.isPending} title="Look for this runner among unclaimed finish crossings">
                            Find
                          </button>
                        )}
                        {r.gunMs !== null && finish && (
                          <button
                            type="button"
                            className="btn small"
                            disabled={clear.isPending}
                            title="This isn't their time: remove it and send the crossing back to review"
                            onClick={() => {
                              if (confirm(`Remove ${r.name}'s finish time? The crossing goes back to review so you can give it to the right runner.`)) clear.mutate({ runnerId: r.runnerId, timingPointId: finish.id });
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <p className="empty">No runners match.</p>}
      </section>
    </div>
  );
}

const KINDS: (ReviewItem['kind'] | 'all')[] = ['all', 'missing', 'unknown', 'low_conf', 'conflict'];

export function ReviewPage() {
  const { raceId = '' } = useParams();
  const qc = useQueryClient();
  const live = useLive(raceId, 8000);
  const [tab, setTab] = useState<'review' | 'results'>('review');
  const [selected, setSelected] = useState<string | null>(null);
  const [kind, setKind] = useState<ReviewItem['kind'] | 'all'>('all');
  const [noBibOnly, setNoBibOnly] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const review = useQuery({ queryKey: ['review', raceId], queryFn: () => api<{ items: ReviewItem[] }>(`/races/${raceId}/review`), refetchInterval: 8000 });
  // Start-only items last: they only affect net times.
  const all = [...(review.data?.items ?? [])].sort((a, b) => Number(isStartOnly(a)) - Number(isStartOnly(b)));
  const items = all.filter((i) => (kind === 'all' || i.kind === kind) && (!noBibOnly || (i.crossing && i.crossing.bibReads.length === 0)));
  const current = items.find((i) => i.id === selected) ?? items[0];
  const gun = live.data?.race.gunTimeMs ?? null;
  const bulk = useMutation({
    mutationFn: (ids: string[]) => api<{ dismissed: number }>(`/races/${raceId}/review/dismiss`, { body: { itemIds: ids } }),
    onSuccess: () => {
      setChecked(new Set());
      qc.invalidateQueries({ queryKey: ['review', raceId] });
      qc.invalidateQueries({ queryKey: liveKey(raceId) });
    },
  });
  const toggle = (id: string) =>
    setChecked((c) => {
      const n = new Set(c);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const count = (k: ReviewItem['kind'] | 'all') => (k === 'all' ? all.length : all.filter((i) => i.kind === k).length);

  return (
    <main className="page">
      <section className="page-head">
        <div>
          <h1>Results &amp; review</h1>
          <p>Anything the cameras couldn’t settle on their own lands here. Every change is logged with who made it.</p>
        </div>
        <div className="inline" role="group" aria-label="View">
          <button type="button" className={`btn ${tab === 'review' ? 'dark' : ''}`} aria-pressed={tab === 'review'} onClick={() => setTab('review')}>
            Needs review · {all.length}
          </button>
          <button type="button" className={`btn ${tab === 'results' ? 'dark' : ''}`} aria-pressed={tab === 'results'} onClick={() => setTab('results')}>
            Results
          </button>
        </div>
      </section>

      {tab === 'results' ? (
        <ResultsTable
          raceId={raceId}
          gun={gun}
          onFind={(id) => {
            setKind('all');
            setSelected(id);
            setTab('review');
          }}
        />
      ) : all.length === 0 ? (
        <div className="card empty">{review.isLoading ? 'Loading…' : 'Nothing to review. Every crossing so far was matched with confidence.'}</div>
      ) : (
        <>
          <div className="inline">
            {KINDS.filter((k) => k === 'all' || count(k) > 0).map((k) => (
              <button key={k} type="button" className={`btn small ${kind === k ? 'dark' : ''}`} aria-pressed={kind === k} onClick={() => { setKind(k); setChecked(new Set()); }}>
                {k === 'all' ? 'All' : kindLabel[k][0]} · {count(k)}
              </button>
            ))}
            <label className="check" style={{ alignItems: 'center', minHeight: 36 }}>
              <input type="checkbox" checked={noBibOnly} onChange={(e) => setNoBibOnly(e.target.checked)} />
              No bib read
            </label>
            <span style={{ marginLeft: 'auto' }} />
            <button type="button" className="btn small" onClick={() => setChecked(new Set(items.map((i) => i.id)))}>
              Select all {items.length}
            </button>
            <button
              type="button"
              className="btn small"
              disabled={checked.size === 0 || bulk.isPending}
              onClick={() => {
                if (confirm(`Dismiss ${checked.size} item(s)? Their crossings are discarded (use for spectators, marshals and passers-by).`)) bulk.mutate([...checked]);
              }}
            >
              Dismiss {checked.size || ''} selected
            </button>
          </div>
          <div className="row">
            <aside className="side-col" style={{ flex: '1 1 300px', gap: 6 }}>
              {items.map((i) => {
                const [label, tone] = labelOf(i);
                const on = current?.id === i.id;
                return (
                  <div key={i.id} className="inline" style={{ flexWrap: 'nowrap', gap: 8, alignItems: 'stretch' }}>
                    <input type="checkbox" aria-label="Select for bulk dismiss" checked={checked.has(i.id)} onChange={() => toggle(i.id)} style={{ width: 18, accentColor: 'var(--accent)' }} />
                    <button
                      type="button"
                      onClick={() => setSelected(i.id)}
                      className="card"
                      style={{ flex: 1, textAlign: 'left', cursor: 'pointer', padding: '10px 12px', border: on ? '2px solid var(--accent)' : undefined, background: on ? 'var(--accent-soft)' : undefined }}
                    >
                      <div className="inline" style={{ justifyContent: 'space-between' }}>
                        <b>{i.runner ? `#${i.runner.bib ?? '—'} ${i.runner.name}` : 'Unknown runner'}</b>
                        <span className={`chip ${tone}`}>{label}</span>
                      </div>
                      <div className="small muted">
                        {i.timingPoint?.name}
                        {i.crossing && gun ? ` · ${formatElapsed(i.crossing.timeMs - gun)}` : ''}
                        {i.crossing ? ` · ${i.crossing.bibReads.map((b) => b.number).join(', ') || 'no bib read'}` : ''}
                      </div>
                    </button>
                  </div>
                );
              })}
              {items.length === 0 && <div className="card empty">Nothing in this filter.</div>}
            </aside>
            <div className="main-col">{current && <ItemDetail key={current.id} item={current} raceId={raceId} gun={gun} />}</div>
          </div>
        </>
      )}
    </main>
  );
}
